import FileSaver from 'file-saver';
import getRemoteCandidateResumePdfUrl from '../../components/Candidates/ResumePdf/Libraries/getRemoteCandidateResumePdfUrl.fun';
import getCandidateResumePdfAnnotations from '../../components/Candidates/ResumePdf/Libraries/processCandidateResumePdfAnnotations.fun';
import AppUI from '../../dictionaries/AppUI.dic';
import {
  Arr
} from '../Array.lib';
import {
  NOT,
  YES
} from '../Boolean.lib';
import openWindow from '../Browser/openWindow.async';
import Core from '../Core';
import {
  Obj
} from '../Object.lib';
import SovrenData from '../SovrenData';
import {
  joinKeyName,
  trim
} from '../String.lib';
import translateToImportContainerUrl from '../Url/translateToImportContainerUrl.fun';

import blobToBinaryObject from '../File/blobToBinaryObject.async';

const cache = {};

/**
 * Fetches a candidate's resume from different sources and formats,
 * optionally downloading it or opening it in a new browser window.
 *
 * @async
 * @function fetchCandidateResume
 *
 * @param {Object} params - Function parameters.
 * @param {'pdf'|'html'|'txt'|'scrubbed'|'highlighted'} [params.key='pdf']
 *   Resume format to request:
 *   - `pdf` → Raw PDF (default)
 *   - `html` → HTML version
 *   - `txt` → Plain text version
 *   - `scrubbed` → PII–removed resume
 *   - `highlighted` → PDF with match highlights (Flesk server)
 *
 * @param {'text'|'blob'|'binary'} [params.type='text']
 *   How the response should be returned:
 *   - `text` → Returns text string (HTML, TXT, scrubbed, etc.)
 *   - `blob` → Returns an object URL created from a Blob (used for preview or opening)
 *   - `binary` → Returns raw binary object (via `blobToBinaryObject`)
 *
 *   ⚠️ `blob` is forced automatically when `download` or `open` is `true`.
 *
 * @param {Object} [params.candidate={}]
 *   Candidate object used to resolve resume URLs when `url` is not provided.
 *   May include: `resumeHtmlUrl`, `resumeTxtUrl`, `resumeScrubbedUrl`, `sovrenDataId`, etc.
 *
 * @param {string} [params.jobId=''] - Used for PDF annotation context.
 * @param {string} [params.employerId=''] - Used for PDF annotation context.
 *
 * @param {string} [params.url='']
 *   Direct URL override. If provided, `candidate` lookup is skipped.
 *
 * @param {boolean} [params.download=false]
 *   If true: triggers automatic file download via `FileSaver.saveAs`.
 *
 * @param {boolean} [params.open=false]
 *   If true: opens the file in a new window (forces `type='blob'`).
 *
 * @param {string} [params.title='Resume']
 *   Window title used when `open` is true.
 *
 * @param {Object} [params.windowOptions={}]
 *   Extra options passed to `openWindow` (width, height, etc.).
 *
 * @param {string} [params.source='fetchCandidateResume']
 *   Metadata passed to server and used for debugging/error reporting.
 *
 * @param {Object} [params.options]
 *   Additional `fetch()` options (headers, credentials, etc.).
 *
 * @returns {Promise<string|Blob|Object|null>}
 *   Depending on configuration:
 *   - `string` → Resume content (text mode)
 *   - `Blob URL` → When `type='blob'` or when `open/download` is used
 *   - `Binary Object` → When `type='binary'`
 *   - `null` → If no URL is available (only when `open=false` and `download=false`)
 *
 * @throws {Error}
 *   - Missing resume URL
 *   - HTTP fetch failure
 *   - Scrubbed file missing when required
 *   - Flesk server highlight errors
 *
 * @description
 * ### MAIN FEATURES
 * - Resolves resume URL based on `key` or overrides it via direct `url`.
 * - Auto-loads scrubbed resume from Sovren when URL is missing.
 * - Supports highlighted PDF mode (server-side annotation).
 * - Converts Fetch response into `text`, `blob`, or binary object.
 * - Cached by URL + candidate/job/employer context for performance.
 * - Can **download** file or **open in new window** with minimal config.
 *
 * ### FORMAT BEHAVIOR MATRIX
 * | key            | server used             | annotated? | output depends on `type` |
 * |----------------|-------------------------|------------|--------------------------|
 * | `pdf`          | import container        | no         | text / blob / binary     |
 * | `html`         | import container        | no         | text only                |
 * | `txt`          | import container        | no         | text only                |
 * | `scrubbed`     | import container/Sovren | no         | text only                |
 * | `highlighted`  | Flesk server (`POST`)   | yes        | blob / binary / URL      |
 *
 * ### CACHE
 * Cached by:
 * ```
 * cacheKey = joinKeyName([url, key, type, jobId, employerId])
 * ```
 */
export default async function fetchCandidateResume({
  key = 'pdf', // 'pdf' | 'html' | 'txt' | 'scrubbed' | 'highlighted'
  type = 'text', // 'blob' | 'text'
  candidate = {},
  jobId = '',
  employerId = '',
  url = '',
  download = false,
  open = false,
  title = 'Resume',
  windowOptions = {},
  source = 'fetchCandidateResume',
  ...options
}) {
  try {

    // PREPARE VARIABLES
    candidate = Obj(candidate);
    url = trim(url);

    // IF not URL is provided, try to obtain one
    // from the candidate object using the key
    if (NOT(url)) {
      if (['pdf', 'highlighted'].includes(key)) {
        url = getRemoteCandidateResumePdfUrl(candidate);
      }
      else if (key === 'html') {
        url = candidate.resumeHtmlUrl;
      }
      else if (key === 'txt') {
        url = candidate.resumeTxtUrl;
      }
      else if (key === 'scrubbed') {
        url = candidate.resumeScrubbedUrl;
        if (
          NOT(trim(url))
          && YES(candidate.sovrenDataId)
        ) {
          const sovrenDataRecord = Obj(
            Arr(
              await SovrenData.get({
                where: {
                  id: candidate.sovrenDataId
                },
                limit: 1,
                fields: [
                  'scrubbedParsedDocument'
                ]
              })
            )[0]
          );
          url = sovrenDataRecord.scrubbedParsedDocument;
        }
        if (NOT(url)) {
          throw new Error(
            `${AppUI.errorMessage.viewScrubbedResume} [${source}]`
          );
        }
      }
    }

    // IF request to open or download
    if (open || download) {
      // IF not URL then throw error
      if (NOT(url)) {
        throw new Error(
          `${AppUI.errorMessage.viewResume} [${source}]`
        );
      }
      // Assign blob type
      type = 'blob';
    }

    // IF not request to open or download
    // and URL doesn't exist, then return null
    if (NOT(url)) {
      return null;
    }

    // translate url to import container url
    url = translateToImportContainerUrl(url);

    // Build cache key
    const cacheKey = joinKeyName([
      url,
      key,
      type,
      jobId,
      employerId
    ]);

    // Assign cache to result
    let result = cache[cacheKey];

    // IF not result
    if (NOT(result)) {

      // IF key is `highlighted`,
      // fetch highlighted resume from Flesk server
      if (key === 'highlighted') {
        console.debug('case: highlighted');
        const URL__FLESK_SERVER = (
          `${process.env.REACT_APP_FMS_API_URL}/get_highlighted_resume/`
        );
        result = await fetch(URL__FLESK_SERVER, {
          ...options,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            source
          },
          body: JSON.stringify({
            access_token: Core.getAccessToken(),
            resume_url: url,
            api_url: Core.getApi(),
            annotations: await getCandidateResumePdfAnnotations({
              candidate,
              jobId,
              employerId,
              source
            }),
          })
        });
      }
      // ELSE fetch resume from API server
      // using Import Container endpoint
      else {
        console.debug('case: not highlighted');
        result = await fetch(url, {
          ...options,
          method: 'GET',
          headers: {
            Authorization: Core.getAccessToken(),
            'Content-Type': 'application/json',
            source
          }
        });
      }

      // IF not ok then throw error
      if (NOT(result.ok)) {
        throw new Error(
          `[${result.status}] Fetch failed ${url} [${source}]`
        );
      }

      // New behavior: return raw Blob without converting to ObjectURL
      if (type === 'blobRaw') {
        result = await result.blob();
        cache[cacheKey] = result;
        return result;
      }
      // Assign result
      if (type === 'binary') {
        // Read as blob
        result = await result.blob();
        // Convert to binary
        result = await blobToBinaryObject(result);
      }
      else {
        // Read as blob or text
        result = await result[type]();
      }

      // Cache result
      cache[cacheKey] = result;
    }

    // IF download
    if (download) {
      FileSaver.saveAs(result, String(url).split("/").pop());
    }

    // IF blob, create Blob URL
    if (type === 'blob') {
      result = URL.createObjectURL(result);
    }

    // IF open
    if (open) {
      openWindow({
        ...windowOptions,
        title,
        url: result,
        source
      });
    }

    // Return result, which could be a Text String, Blob URL, or Binary Object
    return result;
  }
  catch (error) {
    throw error;
  }
}
